import produce from 'immer';
import React, {createContext, type Dispatch, useReducer} from 'react';
import {type DateTime} from 'vega-lite/build/src/datetime';

export enum HeaderCellType {
  CHECKBOX = 'CHECKBOX',
  NAME = 'NAME',
  STORAGE = 'STORAGE',
}

export type TreeNodeType =
  | 'entity'
  | 'project'
  | 'run'
  | 'file'
  | 'artifact'
  | 'artifact_type'
  | 'artifact_version'
  | 'folder';

export type TreeNode = {
  id?: string;
  size: number;
  name: string;
  type: TreeNodeType;
  children: TreeNode[];
};

export type File = {
  id: string;
  name: string;
  url?: string | null;
  sizeBytes: number;
  // Run files always passes updatedAt, ArtifactFiles never does (individual
  // file timestamps aren't very useful for artifacts)
  updatedAt?: string | null;

  // ArtifactFiles may pass either of these, or both
  ref?: string;
  digest?: string;
  selected?: boolean;
  disabled?: boolean;
  artifact?: {
    id: string;
    digest: string;
  } | null;
  storagePolicyConfig?: {
    storageRegion?: string;
    storageLayout?: string;
  };
};

export type Node = {
  files: File[];
  subdirectories: {[key: string]: Node};
  searchable?: boolean;
  selectable?: boolean;
  selected?: boolean;
  disabled?: boolean;
  childSelected?: boolean;
  id?: string;
  type?: TreeNodeType;
  summary?: string;
  fileCount?: number;
  size?: number;
  name?: string;
};

// The LeafMap lets us know the path, type and bytes of all selected objects
export type LeafMap = {
  [id: string]: {type: TreeNodeType; path: readonly string[]; bytes: number};
};

export type StorageTableContextState = {
  filter?: string;
  since?: DateTime;
  selectedIds: Set<string>;
  leafMap: LeafMap;
  filtered: string[];
  selectedBytes: number;
  sortColumn: HeaderCellType;
  isAsc: boolean;
};

export const DEFAULT_IS_ASC = false;

const initialState = (): StorageTableContextState => ({
  selectedIds: new Set<string>(),
  leafMap: {},
  filtered: [],
  selectedBytes: 0,
  sortColumn: HeaderCellType.STORAGE,
  isAsc: DEFAULT_IS_ASC,
});

export const StorageTableContext = createContext<StorageTableContextState>(
  initialState()
);

export const StorageTableDispatchContext = createContext<
  Dispatch<StorageAction>
>(() => {});

interface SelectionPayload {
  ids: string[];
  bytes: number[];
  types: TreeNodeType[];
  path?: string[];
}

export type StorageAction =
  | {
      type: 'select';
      payload: SelectionPayload;
    }
  | {
      type: 'deselect';
      payload: SelectionPayload;
    }
  | {type: 'filter'; payload: string[]}
  | {type: 'clear'}
  | {type: 'setSortColumn'; payload: HeaderCellType}
  | {type: 'setIsAsc'; payload: boolean};

export const reducer = produce(
  (draft: StorageTableContextState, action: StorageAction) => {
    switch (action.type) {
      case 'select':
        // We store a mapping of ids to leaf paths / sizes to render nice
        // confirmation dialogs for files or artifact versions
        if (action.payload.path) {
          // Remove any selected child nodes if a parent node was selected
          Object.keys(draft.leafMap)
            .filter(key => {
              const leafPath = draft.leafMap[key].path;

              // leaf path must be longer than current path if it's a child
              if (leafPath.length <= action.payload.path!.length) {
                return false;
              }

              // leaf must have current path as a prefix if it's a child
              for (let i = 0; i < action.payload.path!.length; i++) {
                if (leafPath[i] !== action.payload.path![i]) {
                  return false;
                }
              }

              return true;
            })
            .forEach(key => {
              draft.selectedIds.delete(key);
              draft.selectedBytes -= draft.leafMap[key].bytes;
              delete draft.leafMap[key];
            });
        }
        action.payload.ids.forEach((id, i) => {
          draft.leafMap[id] = {
            path: action.payload.path!,
            type: action.payload.types[i],
            bytes: action.payload.bytes[i],
          };
          draft.selectedBytes += action.payload.bytes[i];
          draft.selectedIds.add(id);
        });
        return;
      case 'deselect':
        if (action.payload.ids.length === 0) {
          draft.selectedBytes = 0;
          draft.selectedIds.clear();
          draft.leafMap = {};
        } else {
          action.payload.ids.forEach((id, i) => {
            draft.selectedIds.delete(id);
            delete draft.leafMap[id];
            draft.selectedBytes -= action.payload.bytes[i];
          });
        }
        return;
      case 'clear':
        return initialState();
      case 'filter':
        draft.filtered = action.payload;
        return;
      case 'setSortColumn':
        draft.sortColumn = action.payload;
        return;
      case 'setIsAsc':
        draft.isAsc = action.payload;
        return;
      default:
        throw new Error(`Unknown storage action ${JSON.stringify(action)}`);
    }
  },
  initialState()
);

export const StorageTableContextProvider: React.FC = ({children}) => {
  const [state, dispatch] = useReducer(reducer, initialState());

  return (
    <StorageTableContext.Provider value={state}>
      <StorageTableDispatchContext.Provider value={dispatch}>
        {children}
      </StorageTableDispatchContext.Provider>
    </StorageTableContext.Provider>
  );
};
